import type { MixedItemTag } from "@memorang/types";

import type { CurrentView } from "../types";

export const subtreeContainsId = (
	tag: MixedItemTag,
	targetId: number,
): boolean => {
	if (tag.id === targetId) {
		return true;
	}
	if (!tag.children?.length) {
		return false;
	}
	for (const child of tag.children) {
		if (subtreeContainsId(child, targetId)) {
			return true;
		}
	}
	return false;
};

export const findTagById = (
	tags: MixedItemTag[],
	targetId: number,
): MixedItemTag | undefined => {
	for (const top of tags) {
		if (top.id === targetId) {
			return top;
		}
		if (top.children?.length) {
			const found = findTagById(top.children, targetId);
			if (found) {
				return found;
			}
		}
	}
	return undefined;
};

export const findTopLevelAncestor = (
	tags: MixedItemTag[],
	targetId: number,
): MixedItemTag | undefined => {
	for (const top of tags) {
		if (subtreeContainsId(top, targetId)) {
			return top;
		}
	}
	return undefined;
};

export const findTopLevelAncestorType = (
	tags: MixedItemTag[],
	targetId: number,
): string | undefined => {
	const ancestor = findTopLevelAncestor(tags, targetId);
	return ancestor?.type;
};

export const collectDescendantIds = (tag: MixedItemTag): number[] => {
	const ids: number[] = [];
	if (!tag.children?.length) {
		return ids;
	}
	for (const child of tag.children) {
		ids.push(child.id);
		ids.push(...collectDescendantIds(child));
	}
	return ids;
};

export const collectAncestorIds = (
	tags: MixedItemTag[],
	targetId: number,
): number[] => {
	const path: number[] = [];
	const walk = (tag: MixedItemTag): boolean => {
		if (tag.id === targetId) {
			return true;
		}
		if (!tag.children?.length) {
			return false;
		}
		for (const child of tag.children) {
			if (walk(child)) {
				path.push(tag.id);
				return true;
			}
		}
		return false;
	};
	for (const top of tags) {
		if (walk(top)) {
			break;
		}
	}
	return path;
};

export const flattenTagsToDepth = (
	tags: MixedItemTag[],
	maxDepth: number,
): MixedItemTag[] => {
	const out: MixedItemTag[] = [];
	const walk = (tag: MixedItemTag, depth: number) => {
		if (depth > maxDepth) {
			return;
		}
		out.push(tag);
		if (tag.children?.length && depth < maxDepth) {
			for (const child of tag.children) {
				walk(child, depth + 1);
			}
		}
	};
	for (const top of tags) {
		walk(top, 1);
	}
	return out;
};

export const hasRenderableSubtree = (
	tag: MixedItemTag,
	currentView: CurrentView,
): boolean => {
	const selfMatches =
		!!tag.displayType?.includes(currentView) &&
		(tag.numItems || tag.items?.length || 0) > 0;
	if (selfMatches) {
		return true;
	}
	if (tag.children?.length) {
		return tag.children.some((child) =>
			hasRenderableSubtree(child, currentView),
		);
	}
	return false;
};
